import { useState, useEffect, useCallback } from 'react';
import { Activity, AlertTriangle, ChevronDown, ChevronUp, RefreshCw, Lock, TrendingDown } from 'lucide-react';

const API_BASE = 'https://tickertapapi.avetter90.workers.dev';

// ---- RANK BADGE ----
function RankBadge({ rank, total }) {
  const pct = rank / total;
  let color = '#22d3ee';
  let label = `#${rank} of ${total}`;
  if (pct > 0.75) color = '#f87171';
  else if (pct > 0.5) color = '#f59e0b';
  return (
    <span className="text-xs font-mono font-bold px-2 py-0.5 rounded"
      style={{ background: `${color}18`, color, border: `1px solid ${color}44` }}>
      {label}
    </span>
  );
}

// ---- METRIC BAR ----
function MetricBar({ value, peerAvg, lowerIsBetter, label, format }) {
  if (value == null || peerAvg == null) return null;
  const fmt = (v) => format === 'pct' ? `${(v * 100).toFixed(1)}%` : v.toFixed(2) + 'x';
  const isWorse = lowerIsBetter ? value > peerAvg : value < peerAvg;
  const color = isWorse ? '#f87171' : '#22d3ee';
  const maxVal = Math.max(value, peerAvg) * 1.2 || 1;

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-gray-500">{label}</span>
        <div className="flex items-center gap-3">
          <span className="text-gray-600">Peers: {fmt(peerAvg)}</span>
          <span className="font-mono font-semibold" style={{ color }}>{fmt(value)}</span>
        </div>
      </div>
      <div className="relative h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden">
        {/* Peer avg marker */}
        <div className="absolute top-0 h-full w-0.5 bg-gray-600 z-10"
          style={{ left: `${Math.min((peerAvg / maxVal) * 100, 100)}%` }} />
        {/* Your value bar */}
        <div className="absolute top-0 h-full rounded-full transition-all duration-700"
          style={{ width: `${Math.min((value / maxVal) * 100, 100)}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

// ---- PEER ROW ----
function PeerRow({ peer, isYours }) {
  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg ${isYours ? 'border border-[#22d3ee33]' : ''}`}
      style={{ background: isYours ? '#22d3ee08' : 'transparent' }}>
      <span className="text-xs font-mono w-5 text-gray-600 text-right">{peer.rank}</span>
      <span className="text-xs font-mono font-semibold w-16 flex-shrink-0"
        style={{ color: isYours ? '#22d3ee' : '#9ca3af' }}>
        {peer.symbol}
        {isYours && <span className="ml-1 text-xs" style={{ color: '#22d3ee88' }}>←</span>}
      </span>
      <div className="flex-1 h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden">
        <div className="h-full rounded-full"
          style={{
            width: `${Math.min((peer.coverageRatio / 5) * 100, 100)}%`,
            backgroundColor: isYours ? '#22d3ee' : peer.rank <= Math.ceil(peer.total / 2) ? '#34d39966' : '#f8717166'
          }} />
      </div>
      <span className="text-xs font-mono w-10 text-right flex-shrink-0"
        style={{ color: isYours ? '#22d3ee' : '#6b7280' }}>
        {peer.coverageRatio != null ? peer.coverageRatio.toFixed(2) + 'x' : '—'}
      </span>
    </div>
  );
}

// ---- HOLDING CARD ----
function HoldingCard({ data }) {
  const [expanded, setExpanded] = useState(false);
  const { ticker, name, rank, total, coverageRatio, payoutRatio, dividendYield, peerAvgCoverage, peerAvgPayout, peerAvgYield, peers, isWeakest, isBottomHalf } = data;

  const statusColor = isWeakest ? '#f87171' : isBottomHalf ? '#f59e0b' : '#22d3ee';
  const statusLabel = isWeakest ? 'WEAKEST IN GROUP' : isBottomHalf ? 'BELOW AVERAGE' : 'ABOVE AVERAGE';

  return (
    <div className="rounded-xl border overflow-hidden" style={{ background: '#0f0f0f', borderColor: isWeakest ? '#f8717133' : '#1a1a1a' }}>

      {/* Red flag banner */}
      {isWeakest && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-[#f8717133]"
          style={{ background: '#f8717108' }}>
          <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
          <span className="text-xs text-red-400 font-semibold">Weakest dividend payer in peer group — elevated cut risk</span>
        </div>
      )}

      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-sm font-mono font-bold text-gray-100">{ticker}</span>
              <RankBadge rank={rank} total={total} />
            </div>
            <span className="text-xs text-gray-600 truncate">{name}</span>
          </div>
          <span className="text-xs font-semibold px-2 py-0.5 rounded"
            style={{ color: statusColor, background: `${statusColor}18`, border: `1px solid ${statusColor}33` }}>
            {statusLabel}
          </span>
        </div>

        {/* Metrics */}
        <div className="space-y-3">
          <MetricBar
            value={coverageRatio}
            peerAvg={peerAvgCoverage}
            lowerIsBetter={false}
            label="Dividend Coverage"
            format="ratio"
          />
          <MetricBar
            value={payoutRatio}
            peerAvg={peerAvgPayout}
            lowerIsBetter={true}
            label="Payout Ratio"
            format="pct"
          />
          <MetricBar
            value={dividendYield}
            peerAvg={peerAvgYield}
            lowerIsBetter={false}
            label="Dividend Yield"
            format="pct"
          />
        </div>
      </div>

      {/* Peer ranking expand */}
      {peers && peers.length > 0 && (
        <div className="border-t border-[#1a1a1a]">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between px-4 py-2.5 text-xs text-gray-500 hover:text-gray-300 hover:bg-[#111] transition-colors"
          >
            <span>Coverage ranking vs {total - 1} peers</span>
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          {expanded && (
            <div className="px-3 pb-3 space-y-1">
              <div className="flex items-center justify-between px-3 mb-2">
                <span className="text-xs text-gray-700">Symbol</span>
                <span className="text-xs text-gray-700">Coverage Ratio</span>
              </div>
              {peers.map(p => (
                <PeerRow key={p.symbol} peer={p} isYours={p.symbol === ticker} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---- PRO LOCK ----
function ProLock({ onUpgrade }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-12 h-12 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center mx-auto mb-4">
        <Lock className="w-5 h-5 text-gray-400" />
      </div>
      <p className="text-sm font-semibold text-gray-200 mb-1">Pro Feature</p>
      <p className="text-xs text-gray-500 mb-4 max-w-xs">Peer Pulse requires Pro to analyze all your holdings. Free tier shows your first position only.</p>
      <button onClick={onUpgrade}
        className="px-5 py-2 rounded-lg text-xs font-semibold text-black hover:opacity-90 transition-opacity"
        style={{ background: '#22d3ee' }}>
        Upgrade to Pro
      </button>
    </div>
  );
}

// ---- EMPTY STATE ----
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Activity className="w-8 h-8 text-gray-700 mb-3" />
      <p className="text-sm text-gray-500">No holdings to analyze</p>
      <p className="text-xs text-gray-700 mt-1">Add positions to your portfolio first</p>
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function PeerPulse({ session, userTier, portfolioTickers = [], onUpgrade }) {
  const isPro = userTier === 'pro' || userTier === 'premium' || userTier === 'founder';
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // Free tier: only show first ticker
  const tickersToAnalyze = isPro ? portfolioTickers : portfolioTickers.slice(0, 1);

  const fetchData = useCallback(async () => {
    if (!session?.access_token || tickersToAnalyze.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const symbols = tickersToAnalyze.join(',');
      const res = await fetch(`${API_BASE}/api/peer-pulse?symbols=${symbols}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json.results || []);
      setLastFetched(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [session, tickersToAnalyze.join(',')]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const weakest = data.filter(d => d.isWeakest);
  const bottomHalf = data.filter(d => d.isBottomHalf && !d.isWeakest);

  return (
    <div className="p-6 space-y-6" style={{ background: '#0a0a0a', minHeight: '100vh' }}>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-4 h-4" style={{ color: '#22d3ee' }} />
            <h1 className="text-base font-semibold text-gray-100">Peer Pulse</h1>
            {!isPro && (
              <span className="text-xs px-2 py-0.5 rounded font-semibold"
                style={{ background: '#1a1a1a', color: '#f59e0b', border: '1px solid #f59e0b44' }}>PRO</span>
            )}
          </div>
          <p className="text-xs text-gray-500">How your dividend payers rank against their peer group</p>
        </div>
        <div className="flex items-center gap-3">
          {lastFetched && (
            <span className="text-xs text-gray-700">
              {lastFetched.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button onClick={fetchData} disabled={loading}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* No positions */}
      {portfolioTickers.length === 0 && <EmptyState />}

      {portfolioTickers.length > 0 && (
        <>
          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                Fetching peer data for {tickersToAnalyze.length} holding{tickersToAnalyze.length > 1 ? 's' : ''}...
              </div>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-[#1a1a1a]"
              style={{ background: '#0f0f0f' }}>
              <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <span className="text-xs text-red-400">{error}</span>
            </div>
          )}

          {/* Data */}
          {!loading && !error && data.length > 0 && (
            <>
              {/* Summary bar */}
              <div className="grid grid-cols-3 gap-3">
                <div className="px-4 py-3 rounded-lg border border-[#1a1a1a] bg-[#0f0f0f]">
                  <p className="text-xs text-gray-600 uppercase tracking-wider mb-1">Analyzed</p>
                  <p className="text-sm font-mono font-semibold text-gray-200">{data.length} holdings</p>
                </div>
                <div className="px-4 py-3 rounded-lg border border-[#1a1a1a] bg-[#0f0f0f]"
                  style={{ borderColor: weakest.length > 0 ? '#f8717133' : '#1a1a1a' }}>
                  <p className="text-xs text-gray-600 uppercase tracking-wider mb-1">Red Flags</p>
                  <p className="text-sm font-mono font-semibold"
                    style={{ color: weakest.length > 0 ? '#f87171' : '#22d3ee' }}>
                    {weakest.length} weakest in group
                  </p>
                </div>
                <div className="px-4 py-3 rounded-lg border border-[#1a1a1a] bg-[#0f0f0f]"
                  style={{ borderColor: bottomHalf.length > 0 ? '#f59e0b33' : '#1a1a1a' }}>
                  <p className="text-xs text-gray-600 uppercase tracking-wider mb-1">Watch List</p>
                  <p className="text-sm font-mono font-semibold"
                    style={{ color: bottomHalf.length > 0 ? '#f59e0b' : '#6b7280' }}>
                    {bottomHalf.length} below average
                  </p>
                </div>
              </div>

              {/* Red flags first */}
              {weakest.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingDown className="w-3.5 h-3.5 text-red-400" />
                    <span className="text-xs font-semibold text-red-400 uppercase tracking-wider">Red Flags</span>
                  </div>
                  <div className="space-y-3">
                    {weakest.map(d => <HoldingCard key={d.ticker} data={d} />)}
                  </div>
                </div>
              )}

              {/* Rest of holdings */}
              <div>
                {weakest.length > 0 && (
                  <div className="flex items-center gap-2 mb-3">
                    <Activity className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">All Holdings</span>
                  </div>
                )}
                <div className="space-y-3">
                  {data.filter(d => !d.isWeakest).map(d => <HoldingCard key={d.ticker} data={d} />)}
                </div>
              </div>

              {/* Pro upsell if free tier */}
              {!isPro && portfolioTickers.length > 1 && (
                <div className="rounded-xl border border-[#1a1a1a] p-6 text-center" style={{ background: '#0f0f0f' }}>
                  <p className="text-xs text-gray-500 mb-1">
                    Showing 1 of {portfolioTickers.length} holdings
                  </p>
                  <p className="text-xs text-gray-600 mb-4">Upgrade to Pro to analyze all your positions</p>
                  <button onClick={onUpgrade}
                    className="px-5 py-2 rounded-lg text-xs font-semibold text-black hover:opacity-90 transition-opacity"
                    style={{ background: '#22d3ee' }}>
                    Upgrade to Pro
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
